import React, { useState, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import TaskDetailModal from './TaskDetailModal';
import './BoardCalendar.css';

const BoardCalendar = ({ board, token, onUpdate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    getAllTasks();
  }, [board]);

  const getAllTasks = () => {
    const allTasks = [];
    (board.groups || []).forEach(group => {
      (group.items || []).forEach(item => {
        if (item.dueDate || item.startDate) {
          allTasks.push({
            ...item,
            groupName: group.name,
            groupColor: getGroupColor(group),
            boardId: board.id,
            boardName: board.name
          });
        }
      });
    });
    setTasks(allTasks);
  };

  const getGroupColor = (group) => {
    const colors = ['#4F8DFF', '#9B6BFF', '#1DBF73', '#F4A340', '#E24B5B', '#66ccff'];
    const index = (board.groups || []).indexOf(group);
    return colors[index % colors.length];
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startDayOfWeek, year, month };
  };

  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      const taskDate = task.dueDate ? new Date(task.dueDate) : task.startDate ? new Date(task.startDate) : null;
      if (!taskDate) return false;
      return (
        taskDate.getDate() === date.getDate() &&
        taskDate.getMonth() === date.getMonth() &&
        taskDate.getFullYear() === date.getFullYear()
      );
    });
  };

  const { daysInMonth, startDayOfWeek, year, month } = getDaysInMonth(currentDate);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const renderCalendar = () => {
    const days = [];
    
    // Empty cells before first day
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Days of month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayTasks = getTasksForDate(date);
      const isToday = 
        date.getDate() === new Date().getDate() &&
        date.getMonth() === new Date().getMonth() &&
        date.getFullYear() === new Date().getFullYear();
      
      days.push(
        <div key={day} className={`calendar-day ${isToday ? 'today' : ''}`}>
          <div className="day-number">{day}</div>
          <div className="day-tasks">
            {dayTasks.slice(0, 3).map((task, idx) => (
              <div
                key={idx}
                className="calendar-task"
                style={{ backgroundColor: task.groupColor }}
                title={task.name}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTask(task);
                }}
                data-testid="calendar-task-item"
              >
                <span className="task-name-truncate">{task.name}</span>
              </div>
            ))}
            {dayTasks.length > 3 && (
              <div className="more-tasks">+{dayTasks.length - 3} more</div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="board-calendar">
      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={prevMonth}>
          <FiChevronLeft size={20} />
        </button>
        <h2 className="calendar-title">{monthNames[month]} {year}</h2>
        <button className="calendar-nav-btn" onClick={nextMonth}>
          <FiChevronRight size={20} />
        </button>
      </div>
      
      <div className="calendar-weekdays">
        {dayNames.map(day => (
          <div key={day} className="calendar-weekday">{day}</div>
        ))}
      </div>
      
      <div className="calendar-grid">
        {renderCalendar()}
      </div>

      {selectedTask && (
        <TaskDetailModal
          isOpen={true}
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </div>
  );
};

export default BoardCalendar;